import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";

/**
 * Short Waitlist Form Component
 * 
 * Quick 30-second signup form designed for maximum Gen Z conversion.
 * Captures essential data: name, email, age range, motivation, and referral interest.
 * 
 * Features:
 * - Emoji-based motivation selection for engagement
 * - Age range dropdown for segmentation  
 * - Referral program promotion
 * - Mobile-optimized design
 * 
 * @returns {JSX.Element} Waitlist signup form
 */
export const WaitlistForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Form state management
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    ageRange: "",
    motivation: ""
  });

  // Age range options for segmentation
  const ageRanges = [
    "16-18",
    "19-21", 
    "22-24",
    "25-27",
    "28+"
  ];

  // Motivation options with emojis for quick selection
  const motivationOptions = [
    { emoji: "💰", text: "Learn to save", value: "save" },
    { emoji: "📈", text: "Start investing", value: "invest" },
    { emoji: "🎯", text: "Budget better", value: "budget" },
    { emoji: "🚀", text: "Financial freedom", value: "freedom" },
    { emoji: "🤔", text: "Just curious", value: "curious" }
  ];

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.email || !formData.ageRange || !formData.motivation) {
      toast({
        variant: "destructive",
        title: "Missing info",
        description: "Please fill in all fields to join the waitlist! 📝"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Add Supabase integration to store waitlist data
      console.log("Waitlist signup:", formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "You're on the waitlist! 🎉",
        description: "Get ready to make sense of your cents. We'll be in touch soon!"
      });

      // Reset form
      setFormData({
        firstName: "",
        email: "",
        ageRange: "",
        motivation: ""
      });

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Oops! Something went wrong",
        description: "Please try again or contact us if the problem persists."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto border-none shadow-lg bg-background/95 backdrop-blur-sm">
      <CardHeader className="text-center pb-4">
        <CardTitle className="text-2xl font-bold text-foreground">
          Join the Waitlist 🚀
        </CardTitle>
        <p className="text-muted-foreground">
          Get early access + perks. Takes 30 seconds!
        </p>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              First Name
            </label>
            <Input
              type="text"
              placeholder="What should we call you?"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              className="border-border focus:border-primary"
            />
          </div>

          {/* Email Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Email
            </label>
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className="border-border focus:border-primary"
            />
          </div>

          {/* Age Range Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Age Range
            </label>
            <div className="flex flex-wrap gap-2">
              {ageRanges.map((range) => (
                <Badge
                  key={range}
                  variant={formData.ageRange === range ? "default" : "outline"}
                  className={`cursor-pointer transition-all duration-200 ${
                    formData.ageRange === range 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                      : "hover:bg-muted"
                  }`}
                  onClick={() => handleInputChange("ageRange", range)}
                >
                  {range}
                </Badge>
              ))}
            </div>
          </div>

          {/* Motivation Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Why do you want Centse?
            </label>
            <div className="grid grid-cols-1 gap-2">
              {motivationOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant={formData.motivation === option.value ? "default" : "outline"}
                  className={`cursor-pointer transition-all duration-200 p-3 text-left justify-start ${
                    formData.motivation === option.value 
                      ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                      : "hover:bg-muted"
                  }`}
                  onClick={() => handleInputChange("motivation", option.value)}
                >
                  <span className="mr-2">{option.emoji}</span>
                  {option.text}
                </Badge>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-3"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Joining..." : "Join Waitlist 🎯"}
          </Button>

          {/* Quiz Option */}
          <div className="text-center pt-2 border-t border-border">
            <p className="text-sm text-muted-foreground mb-3 mt-3">
              Want a personalized money profile + chance to win £100?
            </p>
            <Button
              variant="outline"
              className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
              onClick={() => navigate("/quiz")}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Take the Full Quiz (2-3 min)
            </Button>
          </div>

          {/* Referral Promotion */}
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground">
              💡 <span className="font-medium">Pro tip:</span> Get 3 friends to sign up and unlock 
              <span className="text-primary font-medium"> early access perks!</span>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};